import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchMyTeams, makeMyTeamPrimary, removeMyTeam, removeMyTeamByName } from "../editHubServices";
import { HubCard, SecondaryBtn, OutlineBadge, useEditLayout } from "../editHubPieces";
import { Btn, ConfirmModal } from "../../../../ForBrands/ui/BrandUI";
import { teamKindLabel } from "../editHubModel";
import { ageGroupOf, displayTeamName, listingLabelOf, stripSeasonSuffix } from "../../../../../utils/teamNames";
import { fillNoteOf } from "../../../../auth/Onboarding/codeTeams";

// The add flow reuses the onboarding team search and roster steps, which
// load the onboarding services; it is fetched only when "Add a team" opens.
const TeamAddFlow = lazy(() => import("./TeamAddFlow"));

// The most teams an athlete can list, the main team included.
export const MAX_TEAMS = 4;

// 'M', 'F' or '' from a team's or the profile's gender, for the team search.
export const genderLetter = (value) => {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return "";
  if (v === "mbb" || /^(m|men|mens|male|boys?)\b/.test(v)) return "M";
  if (v === "wbb" || /^(f|w|women|womens|female|girls?)\b/.test(v)) return "F";
  return "";
};

const keyOf = (t) => `${t.league || ""}|${t.teamId || t.teamName || ""}`;
const sameText = (x, y) => String(x || "").trim().toLowerCase() === String(y || "").trim().toLowerCase();

// What kind of team it is (the server's `kind`: college, scholastic, club,
// pro), from the team itself, else from `known`, a team list that carries
// it (athlete/by-email's allTeams) for one that does not.
const knownTeam = (t, known = []) => (Array.isArray(known) ? known : []).find((k) => k && (k.teamId && t?.teamId
  ? k.teamId === t.teamId && (!k.league || !t.league || k.league === t.league)
  : k.teamName === t?.teamName));
export const kindOf = (t, known = []) => t?.kind || knownTeam(t, known)?.kind || "";
// An 8th grade and under team (the server's `junior`), the same way.
export const juniorOf = (t, known = []) => !!(t?.junior || knownTeam(t, known)?.junior);
// A generic level label says nothing the kind does not.
const GENERIC_LEVELS = new Set(["hs / grassroots", "hs", "college", "pro"]);

// The team's name as people read it (10/19 notes, D15): the name without a
// season CACHE added, with a squad's age group ("Oakland Soldiers 16U"; the
// server sends displayName).
export const teamTitle = (t) => displayTeamName(t) || String(t?.teamName || "").trim();

// The line under a team's name: its kind (College, High school, Club, Pro),
// its league (without a season), a level that adds to the kind (a
// division; an age group the title or the league already says is left
// out) and its coach.
export const teamSubline = (t, known = [], withCoach = true) => {
  const kind = teamKindLabel(kindOf(t, known), juniorOf(t, known));
  const league = stripSeasonSuffix(t.leagueLabel || "");
  const age = ageGroupOf(t);
  const raw = String(t.level || "").trim();
  const repeatsAge = !!age && raw.toLowerCase() === age.toLowerCase() && (teamTitle(t).includes(age) || league.includes(age));
  const level = (kind && GENERIC_LEVELS.has(raw.toLowerCase())) || repeatsAge ? "" : t.level;
  return [kind, league, level, withCoach && t.coachName ? `Coach ${t.coachName}` : ""].filter(Boolean).join(" · ");
};

// The main team first, then the rest in the order the server sent them.
export const orderTeams = (teams) => {
  const list = (Array.isArray(teams) ? teams : []).filter((t) => t && (t.teamName || t.teamId));
  return [...list.filter((t) => t.isPrimary), ...list.filter((t) => !t.isPrimary)];
};

// The other teams on the list with this team's name: two listings of one
// team (Penn State as an NCAA team and as its school's competition
// squad), or two squads of one school. Told apart by league and id, so a
// copy of a listed team (one read before the list changed) is still that
// team.
const twinsOf = (t, list) => {
  const name = teamTitle(t);
  if (!name) return [];
  return (Array.isArray(list) ? list : []).filter((x) => x && keyOf(x) !== keyOf(t) && sameText(teamTitle(x), name));
};

// What tells a team apart from another on the list with the same name: its
// listing (the server's listingLabel: its league, else level words; never
// a data vendor's name, D16). "" for a team whose name is its own.
export const teamQualifier = (t, list) => {
  const twins = twinsOf(t, list);
  if (!twins.length) return "";
  return listingLabelOf(t);
};

// The team's name as the card says it, in its buttons and its questions:
// "Penn State", or "Penn State (NCAA Men's Basketball)" beside another Penn
// State. "" for a team with no name.
export const teamLabel = (t, list) => {
  const name = teamTitle(t);
  const qualifier = name ? teamQualifier(t, list) : "";
  return qualifier ? `${name} (${qualifier})` : name;
};

// The line under a team's name on the card (teamSubline), led by its
// listing when another team on the list has its name and the line does
// not say it yet, so a duplicate can be told apart and removed. The
// listing goes first because a phone cuts the end of a long line.
export const rowSubline = (t, list, known = []) => {
  const line = teamSubline(t, known);
  const listing = twinsOf(t, list).length ? listingLabelOf(t) : "";
  if (!listing || line.split(" · ").some((part) => sameText(part, listing))) return line;
  return [listing, line].filter(Boolean).join(" · ");
};

// The roster step's shape of a listed team (TeamSteps IdentityStep): a
// CACHE Partner team rosters through its CACHE league, any other through
// its own league key.
const rosterTeamOf = (t) => {
  const partner = t.teamSource === "cache_teams" || String(t.league || "").startsWith("cache_");
  return {
    id: t.teamId,
    name: t.teamName,
    ...(t.displayName ? { displayName: t.displayName } : {}),
    leagueName: t.leagueLabel || "",
    teamSource: t.teamSource || (partner ? "cache_teams" : ""),
    ...(partner ? { cacheTeamId: t.teamId, cacheLeague: t.league } : { catalogueKey: t.league }),
  };
};

// A team the athlete can make their main team: the server says so
// (athletes/teams/ canMakePrimary), and it has a team id to bind to (a team
// a name-only coach code listed has none).
export const canMakePrimary = (t) => !!t && !t.isPrimary && t.canMakePrimary === true && !!String(t.teamId || "").trim();

// The Make primary question: what the main team is (the one the profile
// leads with; games from every team count toward the CacheScore), what
// happens to the main team now (replaced when the server says the two are
// the same team from two listings, else kept as another team), and that a
// team no code has confirmed reads Unverified as the main team too.
export const makePrimaryCopy = (t, current, list) => {
  const team = teamLabel(t, list) || "this team";
  const lines = [`Your profile will lead with ${team}. Games from all your teams count toward your CacheScore.`];
  if (current) {
    const main = teamLabel(current, list) || "Your current main team";
    lines.push(t.replacesPrimary
      ? `${main} is the same team from another listing, so it is replaced.`
      : `${main} stays on your profile as another team.`);
  }
  if (!t.verified) lines.push(`${team} shows as unverified until its team code confirms it.`);
  return { title: `Make ${team} your main team?`, body: lines.join(" ") };
};

// The reminder under the list for the teams kept while their roster could
// not be read (pendingRosterPick, no spot yet): their spot is still to
// pick, which links the athlete's game data.
export const spotReminder = (waiting, list) => (waiting.length === 1
  ? `Your roster spot on ${teamLabel(waiting[0], list) || "one of your teams"} is not linked yet. Pick your spot to link your game data.`
  : `Your roster spots on ${waiting.length} teams are not linked yet. Pick your spot on each to link your game data.`);

// Why a Make primary was refused: the server's sentence, else one for its
// code. A request the server could not answer (a 5xx) never shows its
// body's text: the service's own sentence for it (editHubServices).
const PRIMARY_REFUSALS = {
  already_primary: "This team is already your main team.",
  not_on_profile: "This team is no longer on your profile.",
  profile_taken: "Another account has already claimed this player profile.",
  team_required: "Pick the team to make your main team.",
  teams_changed: "Your teams changed while this was saving. Try again.",
};
const primaryRefusal = (e) => {
  const data = e?.data && typeof e.data === "object" ? e.data : {};
  if (e?.status >= 500) return e?.message || "We could not change your main team.";
  if (typeof data.message === "string" && data.message.trim()) return data.message.trim();
  return PRIMARY_REFUSALS[data.error] || e?.message || "We could not change your main team.";
};
// A refusal that means the list was out of date (the team went, another
// session already made it the main team, or the teams changed while the
// switch was saving): the list is read again.
const staleList = (e) => e?.status === 404 || ["not_on_profile", "already_primary", "teams_changed"].includes(e?.data?.error);

// v2 · 04 · "Your teams", every team on the profile (GET athletes/teams/):
// Primary, Verified or Unverified, Make primary and Remove for any team that
// is not the main one, and the two ways to add one: "Add a team" (search,
// then claim your roster spot; with no main team yet the first team becomes
// the main team) and `verifier`, the team code box, which verifies a team
// (one code per team, which every coach of the team shares). A team added
// by search stays unverified until its team code confirms it; games from
// every team count toward the CacheScore, and Make primary (after a
// confirmation that says what happens to the main team now) changes which
// one the profile leads with. Two teams with one name (two listings of one
// team) each say which listing they are. A team kept while its roster
// could not be read (pendingRosterPick) offers "Pick your spot", and a line
// under the list says its spot is still to pick (spotReminder). Titled
// apart from the Teams section, so a screen has one heading named "Teams".
// `onTeamAdded` hears of a team added here, or made the main team (what it
// gets then carries `primaryChanged`, as does the first team added to a
// profile with none, or a spot claimed on the main team: the server filed
// the Teams fields from it): the editor reads the profile again for the
// names it now gives. `beforeTeamWrite` (the editor's) runs before such a
// write. `version` changes when a team was linked elsewhere on the screen
// (the Club and Team fields): the list is read again.
//
// On phones (no Figma frame, D-e6) it is a flat block after Previous teams:
// the list, a full-width 52px "Add a team", then the code box under it. A
// row's actions get a line of their own there, so the name keeps its room.
export const TeamsCard = ({ a = {}, genderName = "", onTeamsChanged, onTeamAdded, verifier = null, version = 0, beforeTeamWrite }) => {
  const { phone } = useEditLayout();
  const [teams, setTeams] = useState(null);
  const [failed, setFailed] = useState(false);
  const [adding, setAdding] = useState(false);
  // The listed team whose roster spot the athlete is picking.
  const [spotFor, setSpotFor] = useState(null);
  const [removing, setRemoving] = useState("");
  // The team waiting on the remove confirmation.
  const [confirming, setConfirming] = useState(null);
  // The team waiting on the Make primary confirmation, and whether that
  // change is on its way (the confirmation stays open, Working..., till then).
  const [promoting, setPromoting] = useState(null);
  const [switching, setSwitching] = useState(false);
  // Where focus goes once the list has changed under it: a team's key (its
  // name in the list, else the list itself when the team is gone).
  const [focusTo, setFocusTo] = useState(null);
  const listRef = useRef(null);
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);

  // Resolves to the ordered list, or null when it could not load.
  const load = useCallback(async () => {
    try {
      const data = await fetchMyTeams();
      if (!live.current) return null;
      const list = orderTeams(data?.teams);
      setTeams(list);
      setFailed(false);
      return list;
    } catch {
      if (!live.current) return null;
      setTeams((prev) => prev || []);
      setFailed(true);
      return null;
    }
  }, []);
  // Reload after a coach or team code links a team (the verified list changes).
  const reloadKey = (Array.isArray(a.verifiedTeams) ? a.verifiedTeams : []).map((t) => t?.teamId || t?.teamName || "").join("|");
  useEffect(() => { load(); }, [load, reloadKey, version]);

  // The button that was focused may be gone with the change (a team made the
  // main one has no Make primary): focus the team's name, else the list.
  useEffect(() => {
    if (focusTo === null) return;
    setFocusTo(null);
    const root = listRef.current;
    if (!root) return;
    const target = Array.from(root.querySelectorAll("[data-team-key]")).find((n) => n.getAttribute("data-team-key") === focusTo);
    (target || root).focus();
  }, [focusTo, teams]);

  // `status` is the profile's status when the server sent one: removing the
  // last verified team puts the profile back to unverified.
  const changed = (next, status) => {
    const list = orderTeams(next);
    setTeams(list);
    if (onTeamsChanged) onTeamsChanged(list, status || "");
    return list;
  };

  // Runs once the athlete confirms in the ConfirmModal.
  const remove = async (t) => {
    setConfirming(null);
    setRemoving(keyOf(t));
    // A team with no id (a coach code from a coach whose team has only a
    // name) is removed by its name; that answer lists only the verified
    // teams, so the full list is read again.
    const byName = !t.teamId;
    try {
      const data = byName
        ? await removeMyTeamByName({ teamName: t.teamName })
        : await removeMyTeam({ teamId: t.teamId, league: t.league });
      if (!live.current) return;
      const next = !byName && Array.isArray(data?.teams)
        ? data.teams
        : (await load()) || (teams || []).filter((x) => keyOf(x) !== keyOf(t));
      if (!live.current) return;
      changed(next, data?.status);
      toast.success("Team removed.");
    } catch (e) {
      toast.error(e?.message || "We could not remove this team.");
    } finally {
      if (live.current) setRemoving("");
    }
  };

  // Runs once the athlete confirms Make primary. The server answers with the
  // whole list (the old main team kept as another team, or replaced); the
  // editor then reads the profile again (onTeamAdded), as after Add a team,
  // since the main team decides the stats, the score and the team fields.
  // The switch re-files the Teams fields on the server, so the editor's
  // beforeTeamWrite runs first (a save waiting to go lands before it, and
  // none sends the old names after it); a refused switch puts them back.
  const makePrimary = async (t) => {
    if (switching) return;
    const label = teamLabel(t, teams) || "This team";
    setSwitching(true);
    let undo = null;
    try {
      if (typeof beforeTeamWrite === "function") undo = await beforeTeamWrite();
      const data = await makeMyTeamPrimary({ teamId: t.teamId, league: t.league });
      undo = null;
      if (!live.current) return;
      const next = Array.isArray(data?.teams) ? data.teams : await load();
      if (!live.current) return;
      setPromoting(null);
      const list = next ? changed(next) : teams || [];
      setFocusTo(keyOf(list.find((x) => x.isPrimary) || t));
      toast.success(String(data?.message || "").trim() || `${label} is now your main team.`);
      const note = fillNoteOf(data);
      if (note) toast.info(note);
      if (onTeamAdded) onTeamAdded({ ...data, primaryChanged: true });
    } catch (e) {
      // Refused: the names go with saves as before.
      if (typeof undo === "function") undo();
      if (!live.current) return;
      setPromoting(null);
      toast.error(primaryRefusal(e));
      if (staleList(e)) {
        const was = (teams || []).find((x) => x.isPrimary);
        const fresh = await load();
        if (!live.current || !fresh) return;
        changed(fresh);
        setFocusTo(keyOf(t));
        // Another session changed the main team meanwhile (this one, or
        // another): the profile changed too, so the editor reads it again.
        const now = fresh.find((x) => x.isPrimary);
        if (onTeamAdded && now && (!was || keyOf(now) !== keyOf(was))) onTeamAdded({ teams: fresh, primaryChanged: true });
      }
    } finally {
      if (live.current) setSwitching(false);
    }
  };

  // `primary` is the main team before the add: with none, the team added is
  // the main team now, and the server filed it in the Club or Team field and
  // filled the sport, gender and level, so the editor reads every Teams
  // field again (as after Make primary), never saving the old names back.
  const onAdded = (data) => {
    setAdding(false);
    if (Array.isArray(data?.teams)) changed(data.teams, data?.status); else load();
    if (onTeamAdded) onTeamAdded(primary ? data : { ...data, primaryChanged: true });
    toast.success("Team added. It shows as unverified until your coach confirms it.");
    const note = fillNoteOf(data);
    if (note) toast.info(note);
  };

  // The athlete picked their spot on a team they kept while its roster could
  // not be read. A spot on the main team binds it again (its Teams fields),
  // which can answer with the gender note too.
  const onSpot = (data) => {
    const team = spotFor;
    setSpotFor(null);
    const next = Array.isArray(data?.teams) ? data.teams : null;
    if (next) changed(next, data?.status); else load();
    if (onTeamAdded) onTeamAdded(team?.isPrimary ? { ...data, primaryChanged: true } : data);
    // Said only when the list the server sent shows the spot (or none came).
    const now = team && next ? next.find((x) => x && x.teamId === team.teamId && (!x.league || !team.league || x.league === team.league)) : null;
    if (!now || String(now.playerId || "").trim()) toast.success("Your roster spot is linked.");
    const note = fillNoteOf(data);
    if (note) toast.info(note);
  };

  const list = teams || [];
  const primary = list.find((t) => t.isPrimary);
  const full = list.length >= MAX_TEAMS;
  // Teams whose roster spot is still to pick (kept while the roster was down).
  const waiting = list.filter((t) => t.pendingRosterPick && !String(t.playerId || "").trim());
  const gender = genderLetter(primary?.gender) || genderLetter(genderName);
  const promoteCopy = promoting ? makePrimaryCopy(promoting, primary, list) : null;

  // Pick your spot (a team kept while its roster could not be read), then
  // Make primary (a small secondary button; 40px on phones) and Remove, for
  // a team that is not the main one.
  const actionsOf = (t) => {
    const label = teamLabel(t, list);
    const spot = t.pendingRosterPick && !t.playerId && (
      <Btn variant="secondary" size={phone ? "md" : "sm"} onClick={() => { setAdding(false); setSpotFor(t); }} disabled={!!removing || !!spotFor} aria-label={`Pick your spot on ${label || "this team"}`}>
        Pick your spot
      </Btn>
    );
    if (t.isPrimary) return spot || null;
    return (
      <>
        {spot}
        {canMakePrimary(t) && (
          <Btn variant="secondary" size={phone ? "md" : "sm"} onClick={() => setPromoting(t)} disabled={!!removing} aria-label={`Make ${label || "this team"} primary`}>
            Make primary
          </Btn>
        )}
        <button type="button" onClick={() => setConfirming(t)} disabled={!!removing} aria-label={`Remove ${label || "team"}`} className="bm-label-l text-bm-muted hover:text-bm-critical disabled:opacity-50">
          {removing === keyOf(t) ? "Removing..." : "Remove"}
        </button>
      </>
    );
  };

  return (
    <HubCard title="Your teams" desc={`Every team you play on, up to ${MAX_TEAMS}. Games from all of them count toward your CacheScore. Your team code verifies a team.`}>
      {teams === null ? (
        <span className="bm-body-m text-bm-muted">Loading your teams...</span>
      ) : list.length === 0 ? (
        <p className="bm-body-m text-bm-muted">{failed ? "We could not load your teams. Try again later." : "No team yet. Add your team, or enter your team code."}</p>
      ) : (
        <ul ref={listRef} tabIndex={-1} className="flex flex-col divide-y divide-bm-mutedbg rounded-[12px] border border-bm-mutedbg outline-none" aria-label="Your teams">
          {list.map((t) => {
            const sub = rowSubline(t, list, a.allTeams);
            const actions = actionsOf(t);
            return (
              <li key={keyOf(t)} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span tabIndex={-1} data-team-key={keyOf(t)} className="truncate bm-label-l text-bm-ink outline-none">{teamTitle(t) || "Team"}</span>
                  {sub && <span className="truncate bm-body-s text-bm-muted">{sub}</span>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {t.isPrimary && <OutlineBadge>Primary</OutlineBadge>}
                  <OutlineBadge>{t.verified ? "Verified" : "Unverified"}</OutlineBadge>
                  {!phone && actions}
                </div>
                {phone && actions && <div className="flex w-full flex-wrap items-center gap-4">{actions}</div>}
              </li>
            );
          })}
        </ul>
      )}
      {waiting.length > 0 && !spotFor && !adding && <p className="bm-body-s text-bm-muted">{spotReminder(waiting, list)}</p>}
      {spotFor ? (
        <div className="flex flex-col gap-4 rounded-[12px] bg-bm-subtle p-4">
          <Suspense fallback={<span className="bm-body-m text-bm-muted">Loading...</span>}>
            <TeamAddFlow athlete={a} gender={gender} slot="" initialTeam={rosterTeamOf(spotFor)} prepare={spotFor.isPrimary ? beforeTeamWrite : undefined} onAdded={onSpot} onCancel={() => setSpotFor(null)} />
          </Suspense>
        </div>
      ) : adding ? (
        <div className="flex flex-col gap-4 rounded-[12px] bg-bm-subtle p-4">
          <Suspense fallback={<span className="bm-body-m text-bm-muted">Loading...</span>}>
            <TeamAddFlow athlete={a} gender={gender} hasMain={!!primary} prepare={primary ? undefined : beforeTeamWrite} onAdded={onAdded} onCancel={() => setAdding(false)} />
          </Suspense>
        </div>
      ) : (
        // The code box does not depend on the list: it works while the list
        // loads or when it could not load. "Add a team" works with no main
        // team too: the first team added becomes the main team.
        <div className="flex flex-col gap-3">
          {phone ? (
            <div className="flex flex-col gap-3">
              {teams !== null && <Btn variant="secondary" size="lg" className="w-full" onClick={() => setAdding(true)} disabled={full}>Add a team</Btn>}
              {verifier && <div className="min-w-0">{verifier}</div>}
            </div>
          ) : (
            <div className="flex flex-wrap items-start gap-3">
              {teams !== null && <SecondaryBtn onClick={() => setAdding(true)} disabled={full}>Add a team</SecondaryBtn>}
              {verifier && <div className="min-w-0 flex-1">{verifier}</div>}
            </div>
          )}
          {teams !== null && (
            <span className="bm-body-s text-bm-tertiary">
              {full ? `You have ${MAX_TEAMS} teams. Remove one to add another.` : primary ? "A team you add stays unverified until its team code confirms it." : "The first team you add becomes your main team."}
            </span>
          )}
        </div>
      )}
      {confirming && (
        <ConfirmModal
          title="Remove this team"
          body={`Remove ${teamLabel(confirming, list) || "this team"} from your teams?${confirming.verified ? " Its team code is needed to verify it again." : ""}`}
          ackLabel="I understand this team comes off my profile."
          confirmLabel="Remove team"
          tone="danger"
          onConfirm={() => remove(confirming)}
          onClose={() => setConfirming(null)}
        />
      )}
      {promoting && (
        <ConfirmModal
          title={promoteCopy.title}
          body={promoteCopy.body}
          requireAck={false}
          confirmLabel="Make primary"
          cancelLabel="Cancel"
          busy={switching}
          dismissible={!switching}
          onConfirm={() => makePrimary(promoting)}
          onClose={() => { if (!switching) setPromoting(null); }}
        />
      )}
    </HubCard>
  );
};
